// ExplorerSidebar — the one folder-tree explorer rail shared by the
// Dashboards, Templates, Reports, and Docs pages. Presentational: the parent
// owns selection + expansion + filter and passes them in. Each row carries a
// right-click context menu whose entries the parent supplies, so the
// page-specific verbs (new dashboard, upload template, …) live on the page
// while the tree rendering, selection states, indentation, count badge, and
// test-id scheme are defined exactly once here.
//
// `navigationMode="drilldown"` (Docs) replaces inline expand/collapse with a
// click-through list of the current folder's direct children. Search still
// uses filterExplorerTree and flattens the matches. Other pages keep `expand`.
//
// Folders and items share one slash-path namespace; `buildExplorerTree`
// derives the tree from those paths. A folder occupies its own node; an item
// is a leaf inside the node named by its path's parent segments. The displayed
// folder label prefers `folder.name` when a real folder row backs the node,
// falling back to the path segment for "implicit" folders (path slots that
// exist only because an item references them).
//
// Pages can opt out with `collapsible={false}`. Collapse is on by default
// so Dashboards, Templates, Reports, and Docs share the same edge handle.
// `collapseStorageKey` persists the preference across remounts.

import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Folder,
  MoreHorizontal,
  Search,
  type LucideIcon
} from 'lucide-react'

import { Button } from '../ui/button'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger
} from '../ui/context-menu'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { Input } from '../ui/input'
import { cn } from '../../lib/utils'
import { designPixelsToRem } from '../../lib/ui-density'
import {
  buildExplorerTree,
  filterExplorerTree,
  findFolderNode,
  flattenExplorerTree,
  isPathPrefix,
  orderPinnedChildKeys,
  parentPathOf,
  type ExplorerTreeNode,
  type PathItem
} from './tree'

const EXPLORER_DEFAULT_WIDTH = 248
const EXPLORER_MIN_WIDTH = 200
const EXPLORER_MAX_WIDTH = 480
const EXPLORER_KEYBOARD_RESIZE_STEP = 16

/**
 * The current selection a page passes to the explorer. A page with its own
 * selection vocabulary (e.g. `{kind:'dashboard'}`) maps onto this in its
 * adapter, so selection rendering is identical across pages.
 */
export type ExplorerSelection =
  | { kind: 'folder'; path: string }
  | { kind: 'item'; id: string }
  | { kind: 'none' }

/** One context-menu entry. The page builds these from its own handlers. */
export interface ExplorerMenuEntry {
  icon: LucideIcon
  label: string
  onSelect: () => void
  /** Render a separator above this entry — used to set off destructive ops. */
  separatorBefore?: boolean
  variant?: 'default' | 'destructive'
  shortcut?: string
  disabled?: boolean
}

/** One header icon button (a page-specific verb like "new folder" / "upload"). */
export interface ExplorerHeaderButton {
  icon: LucideIcon
  /** aria-label + title for the icon button. */
  label: string
  onClick: () => void
}

type DragPayload = { kind: 'folder'; path: string } | { kind: 'item'; id: string }

export interface ExplorerSidebarProps<F extends PathItem, I extends PathItem> {
  heading: string
  /** `role="tree"` aria-label, e.g. "Dashboard folders". */
  ariaLabel: string
  folders: readonly F[]
  items: readonly I[]
  /** Leaf icon for item rows (folders always use the Folder icon). */
  itemIcon: LucideIcon
  /** Rows get `${testIdPrefix}-folder-${path}` / `${testIdPrefix}-item-${id}`. */
  testIdPrefix: string
  selection: ExplorerSelection
  expanded: Set<string>
  filter: string
  isLoading?: boolean
  /** Copy shown in the empty body (when not loading). */
  emptyHint: string
  /** Header icon buttons, rendered right-aligned in passed order. */
  headerButtons: readonly ExplorerHeaderButton[]
  /** Context-menu entries for a folder node; the folder-name label is added here. */
  folderMenu: (node: ExplorerTreeNode<F, I>) => readonly ExplorerMenuEntry[]
  /** Context-menu entries for an item leaf; the item-name label is added here. */
  itemMenu: (item: I) => readonly ExplorerMenuEntry[]
  /** Context-menu entries for the root / empty body (under a "Root" label). */
  rootMenu: readonly ExplorerMenuEntry[]
  onToggle: (path: string) => void
  onSelectFolder: (path: string) => void
  onSelectItem: (id: string) => void
  onFilterChange: (filter: string) => void
  /** Rendered above the header — hosts the page's tab toggle. */
  topSlot?: ReactNode
  /**
   * Optional trailing node on each item row (format badge, lock icon, …).
   * Rendered before the kebab so the name still truncates.
   */
  itemTrailing?: (item: I) => ReactNode
  /**
   * Optional trailing node on each folder row (e.g. an "Auto" badge).
   * Rendered before the kebab so the name still truncates.
   */
  folderTrailing?: (node: ExplorerTreeNode<F, I>) => ReactNode
  /**
   * `expand` (default) keeps the inline expand/collapse tree used by
   * Dashboards, Templates, and Reports. `drilldown` replaces a folder
   * click with a click-through: the sidebar lists that folder's direct
   * children and a back-chevron + breadcrumb sit above the list.
   */
  navigationMode?: 'expand' | 'drilldown'
  /**
   * Folder paths pinned to the top of the visible root (and the root
   * only). Unknown paths are ignored. Used by Docs for Ingested Documents.
   */
  pinnedFolderPaths?: readonly string[]
  /**
   * When false, the rail stays a fixed 15.5rem column with no edge handle.
   * Defaults to true so Dashboards, Templates, Reports, and Docs share
   * the same collapse control.
   */
  collapsible?: boolean
  /**
   * localStorage key for the open/closed preference. Defaults to
   * `ontology.explorerSidebar.${testIdPrefix}.open`. Dashboards and Templates
   * pass a shared key so a tab swap (which remounts this tree) keeps the
   * preference.
   */
  collapseStorageKey?: string
  /**
   * When set, item rows are draggable onto folders / the root gutter.
   * Templates and Reports omit this and get no drag-and-drop.
   */
  onMoveItem?: (item: I, destinationFolderPath: string) => void
  /** When set, folder rows are draggable onto other folders / the root gutter. */
  onMoveFolder?: (fromPath: string, destinationFolderPath: string) => void
  /** Locked folders (e.g. Agent Insights) cannot be dragged or dropped onto. */
  isLockedFolder?: (path: string) => boolean
  /** Locked items cannot be dragged. */
  isLockedItem?: (item: I) => boolean
  /** Folder rows pending a cut, rendered dimmed. */
  dimmedFolderPaths?: ReadonlySet<string>
  /** Item rows pending a cut, rendered dimmed. */
  dimmedItemIds?: ReadonlySet<string>
}

interface RowContext<F extends PathItem, I extends PathItem> {
  itemIcon: LucideIcon
  itemTrailing?: (item: I) => ReactNode
  folderTrailing?: (node: ExplorerTreeNode<F, I>) => ReactNode
  testIdPrefix: string
  selection: ExplorerSelection
  expanded: Set<string>
  filterActive: boolean
  drilldown: boolean
  folderMenu: (node: ExplorerTreeNode<F, I>) => readonly ExplorerMenuEntry[]
  itemMenu: (item: I) => readonly ExplorerMenuEntry[]
  onToggle: (path: string) => void
  onSelectFolder: (path: string) => void
  onSelectItem: (id: string) => void
  onMoveItem?: (item: I, destinationFolderPath: string) => void
  onMoveFolder?: (fromPath: string, destinationFolderPath: string) => void
  isLockedFolder?: (path: string) => boolean
  isLockedItem?: (item: I) => boolean
  dimmedFolderPaths?: ReadonlySet<string>
  dimmedItemIds?: ReadonlySet<string>
  itemsById: ReadonlyMap<string, I>
  dragging: DragPayload | null
  draggingRef: { current: DragPayload | null }
  setDragging: (payload: DragPayload | null) => void
  dropTarget: string | null
  setDropTarget: (path: string | null) => void
}

function renderContextEntries(entries: readonly ExplorerMenuEntry[]): ReactNode {
  return entries.map((entry) => {
    const Icon = entry.icon
    return (
      <Fragment key={entry.label}>
        {entry.separatorBefore ? <ContextMenuSeparator /> : null}
        <ContextMenuItem
          variant={entry.variant}
          disabled={entry.disabled}
          onSelect={entry.disabled === true ? undefined : entry.onSelect}
        >
          <Icon />
          {entry.label}
          {entry.shortcut ? (
            <span className="ml-auto text-xs tracking-widest text-muted-foreground">
              {entry.shortcut}
            </span>
          ) : null}
        </ContextMenuItem>
      </Fragment>
    )
  })
}

function renderDropdownEntries(entries: readonly ExplorerMenuEntry[]): ReactNode {
  return entries.map((entry) => {
    const Icon = entry.icon
    return (
      <Fragment key={entry.label}>
        {entry.separatorBefore ? <DropdownMenuSeparator /> : null}
        <DropdownMenuItem
          variant={entry.variant}
          disabled={entry.disabled}
          onSelect={entry.disabled === true ? undefined : entry.onSelect}
        >
          <Icon />
          {entry.label}
          {entry.shortcut ? <DropdownMenuShortcut>{entry.shortcut}</DropdownMenuShortcut> : null}
        </DropdownMenuItem>
      </Fragment>
    )
  })
}

function RowKebab({
  testId,
  label,
  entries,
  forceVisible
}: {
  testId: string
  label: string
  entries: readonly ExplorerMenuEntry[]
  forceVisible?: boolean
}) {
  if (entries.length === 0) return null
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="icon-xs"
          variant="ghost"
          data-testid={testId}
          aria-label={`${label} actions`}
          title={`${label} actions`}
          className={cn(
            'size-4.5 shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
            forceVisible && 'opacity-100'
          )}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <MoreHorizontal className="size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {renderDropdownEntries(entries)}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function loadExplorerOpen(storageKey: string | undefined): boolean {
  if (!storageKey) return true
  try {
    const stored = window.localStorage.getItem(storageKey)
    if (stored === 'false') return false
    if (stored === 'true') return true
  } catch {
    // localStorage unavailable — fall through to default
  }
  return true
}

function persistExplorerOpen(storageKey: string | undefined, open: boolean): void {
  if (!storageKey) return
  try {
    window.localStorage.setItem(storageKey, open ? 'true' : 'false')
  } catch {
    // best-effort; persistence is a convenience, not a correctness requirement
  }
}

function clampExplorerWidth(width: number): number {
  return Math.min(EXPLORER_MAX_WIDTH, Math.max(EXPLORER_MIN_WIDTH, width))
}

function loadExplorerWidth(storageKey: string | undefined): number {
  if (!storageKey) return EXPLORER_DEFAULT_WIDTH
  try {
    const stored = Number(window.localStorage.getItem(storageKey))
    if (Number.isFinite(stored) && stored > 0) return clampExplorerWidth(stored)
  } catch {
    // localStorage unavailable — fall through to default
  }
  return EXPLORER_DEFAULT_WIDTH
}

function persistExplorerWidth(storageKey: string | undefined, width: number): void {
  if (!storageKey) return
  try {
    window.localStorage.setItem(storageKey, String(width))
  } catch {
    // best-effort; persistence is a convenience, not a correctness requirement
  }
}

function ExplorerRailToggle({
  open,
  heading,
  testIdPrefix,
  onToggle
}: {
  open: boolean
  heading: string
  testIdPrefix: string
  onToggle: () => void
}) {
  const label = open ? `Collapse ${heading} explorer` : `Expand ${heading} explorer`
  return (
    <Button
      type="button"
      size="icon-xs"
      variant="outline"
      onClick={onToggle}
      aria-label={label}
      aria-expanded={open}
      title={label}
      data-testid={open ? `${testIdPrefix}-collapse` : `${testIdPrefix}-expand`}
      className={cn(
        'size-6 rounded-full shadow-sm',
        'transition-colors duration-200 ease-out motion-reduce:transition-none',
        open
          ? 'border-line bg-bg-elev text-ink-3 hover:bg-bg-sunk hover:text-ink'
          : 'border-brand/40 bg-brand-soft text-brand hover:bg-brand-soft hover:text-brand'
      )}
    >
      {open ? <ChevronLeft className="size-3" /> : <ChevronRight className="size-3" />}
    </Button>
  )
}

function canDropOnFolder(
  dragging: DragPayload | null,
  destPath: string,
  isLockedFolder?: (path: string) => boolean
): boolean {
  if (dragging === null) return false
  if (isLockedFolder?.(destPath)) return false
  if (dragging.kind === 'folder' && isPathPrefix(dragging.path, destPath)) return false
  return true
}

function FolderRow<F extends PathItem, I extends PathItem>({
  node,
  depth,
  ctx
}: {
  node: ExplorerTreeNode<F, I>
  depth: number
  ctx: RowContext<F, I>
}) {
  const childKeys = Object.keys(node.children)
  const hasChildren = childKeys.length > 0 || node.items.length > 0
  const isOpen = !ctx.drilldown && (ctx.filterActive || ctx.expanded.has(node.path))
  const isSelected = ctx.selection.kind === 'folder' && ctx.selection.path === node.path
  const folder = node.folder
  const displayName = (folder?.name ?? node.name) || '/'
  const locked = ctx.isLockedFolder?.(node.path) === true
  const draggable = Boolean(ctx.onMoveFolder) && !locked
  const isDropTarget = ctx.dropTarget === node.path
  const menuEntries = ctx.folderMenu(node)

  const handleClick = (): void => {
    ctx.onSelectFolder(node.path)
    if (!ctx.drilldown && hasChildren && !ctx.filterActive) ctx.onToggle(node.path)
  }

  return (
    <div>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            onClick={handleClick}
            role="treeitem"
            aria-expanded={hasChildren ? isOpen : undefined}
            aria-selected={isSelected}
            aria-dropeffect={
              ctx.dragging && canDropOnFolder(ctx.dragging, node.path, ctx.isLockedFolder)
                ? 'move'
                : undefined
            }
            data-testid={`${ctx.testIdPrefix}-folder-${node.path}`}
            draggable={draggable}
            onDragStart={(event) => {
              if (!draggable) return
              event.dataTransfer.effectAllowed = 'move'
              event.dataTransfer.setData('text/plain', node.path)
              ctx.draggingRef.current = { kind: 'folder', path: node.path }
              ctx.setDragging({ kind: 'folder', path: node.path })
            }}
            onDragEnd={() => {
              ctx.draggingRef.current = null
              ctx.setDragging(null)
              ctx.setDropTarget(null)
            }}
            onDragOver={(event) => {
              if (!canDropOnFolder(ctx.draggingRef.current, node.path, ctx.isLockedFolder)) return
              event.preventDefault()
              event.stopPropagation()
              event.dataTransfer.dropEffect = 'move'
              if (ctx.dropTarget !== node.path) ctx.setDropTarget(node.path)
            }}
            onDragLeave={() => {
              if (ctx.dropTarget === node.path) ctx.setDropTarget(null)
            }}
            onDrop={(event) => {
              event.preventDefault()
              event.stopPropagation()
              const dragging = ctx.draggingRef.current
              ctx.draggingRef.current = null
              ctx.setDropTarget(null)
              ctx.setDragging(null)
              if (!canDropOnFolder(dragging, node.path, ctx.isLockedFolder) || dragging === null) {
                return
              }
              if (dragging.kind === 'folder') {
                ctx.onMoveFolder?.(dragging.path, node.path)
                return
              }
              const item = ctx.itemsById.get(dragging.id)
              if (item) ctx.onMoveItem?.(item, node.path)
            }}
            className={cn(
              'group flex cursor-pointer items-center gap-1 rounded py-1 pr-1.5 font-mono text-xs',
              isSelected ? 'bg-brand-soft text-ink' : 'text-ink-2 hover:bg-bg-sunk/60',
              isDropTarget && 'ring-1 ring-brand',
              ctx.dimmedFolderPaths?.has(node.path) === true ||
                (draggable && ctx.dragging?.kind === 'folder' && ctx.dragging.path === node.path)
                ? 'opacity-60'
                : null
            )}
            style={{ paddingLeft: designPixelsToRem(6 + depth * 12) }}
          >
            {hasChildren ? (
              <ChevronRight
                className={cn(
                  'size-2.5 shrink-0 transition-transform duration-100',
                  !ctx.drilldown && isOpen && 'rotate-90'
                )}
                aria-hidden
              />
            ) : (
              <span className="w-2.5 shrink-0" />
            )}
            <Folder
              className={cn(
                'size-2.75 shrink-0',
                locked || isSelected ? 'text-brand' : 'text-ink-3'
              )}
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate">
              {displayName}
              {ctx.drilldown && ctx.filterActive ? (
                <span className="ml-1 text-10 text-ink-3">{node.path}</span>
              ) : null}
            </span>
            {ctx.folderTrailing?.(node)}
            <RowKebab
              testId={`${ctx.testIdPrefix}-folder-menu-${node.path}`}
              label={folder ? folder.name : node.name || 'Folder'}
              entries={menuEntries}
              forceVisible={isSelected}
            />
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuLabel>{folder ? folder.name : node.name || 'Root'}</ContextMenuLabel>
          {renderContextEntries(menuEntries)}
        </ContextMenuContent>
      </ContextMenu>
      {!ctx.drilldown && isOpen && hasChildren ? (
        <div role="group">
          {childKeys.map((k) => (
            <FolderRow
              key={node.children[k].path}
              node={node.children[k]}
              depth={depth + 1}
              ctx={ctx}
            />
          ))}
          {node.items.map((item) => (
            <ItemRow key={item.id} item={item} depth={depth + 1} ctx={ctx} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function ItemRow<F extends PathItem, I extends PathItem>({
  item,
  depth,
  ctx
}: {
  item: I
  depth: number
  ctx: RowContext<F, I>
}) {
  const ItemIcon = ctx.itemIcon
  const isSelected = ctx.selection.kind === 'item' && ctx.selection.id === item.id
  const locked = ctx.isLockedItem?.(item) === true
  const draggable = Boolean(ctx.onMoveItem) && !locked
  const menuEntries = ctx.itemMenu(item)
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          onClick={() => ctx.onSelectItem(item.id)}
          role="treeitem"
          aria-selected={isSelected}
          data-testid={`${ctx.testIdPrefix}-item-${item.id}`}
          draggable={draggable}
          onDragStart={(event) => {
            if (!draggable) return
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', item.id)
            ctx.draggingRef.current = { kind: 'item', id: item.id }
            ctx.setDragging({ kind: 'item', id: item.id })
          }}
          onDragEnd={() => {
            ctx.draggingRef.current = null
            ctx.setDragging(null)
            ctx.setDropTarget(null)
          }}
          className={cn(
            'group flex cursor-pointer items-center gap-1 rounded py-1 pr-1.5 font-mono text-xs',
            isSelected ? 'bg-brand-soft text-ink' : 'text-ink-2 hover:bg-bg-sunk/60',
            ctx.dimmedItemIds?.has(item.id) === true ||
              (draggable && ctx.dragging?.kind === 'item' && ctx.dragging.id === item.id)
              ? 'opacity-60'
              : null
          )}
          style={{ paddingLeft: designPixelsToRem(6 + depth * 12) }}
        >
          <span className="w-2.5 shrink-0" />
          <ItemIcon className="size-2.75 shrink-0 text-ink-3" aria-hidden />
          <span className="min-w-0 flex-1 truncate">
            {item.name}
            {ctx.drilldown && ctx.filterActive ? (
              <span className="ml-1 text-10 text-ink-3">{item.path}</span>
            ) : null}
          </span>
          {ctx.itemTrailing?.(item)}
          <RowKebab
            testId={`${ctx.testIdPrefix}-item-menu-${item.id}`}
            label={item.name}
            entries={menuEntries}
            forceVisible={isSelected}
          />
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuLabel>{item.name}</ContextMenuLabel>
        {renderContextEntries(menuEntries)}
      </ContextMenuContent>
    </ContextMenu>
  )
}

function breadcrumbSegments(path: string): { name: string; path: string }[] {
  if (path === '') return []
  const out: { name: string; path: string }[] = []
  let acc = ''
  for (const segment of path.split('/')) {
    if (segment.length === 0) continue
    acc = acc === '' ? segment : `${acc}/${segment}`
    out.push({ name: segment, path: acc })
  }
  return out
}

function drilldownFolderPath<I extends PathItem>(
  selection: ExplorerSelection,
  itemsById: ReadonlyMap<string, I>
): string {
  if (selection.kind === 'folder') return selection.path
  if (selection.kind === 'item') {
    const item = itemsById.get(selection.id)
    return item ? parentPathOf(item.path) : ''
  }
  return ''
}

function ExplorerSidebar<F extends PathItem, I extends PathItem>({
  heading,
  ariaLabel,
  folders,
  items,
  itemIcon,
  itemTrailing,
  folderTrailing,
  testIdPrefix,
  selection,
  expanded,
  filter,
  isLoading,
  emptyHint,
  headerButtons,
  folderMenu,
  itemMenu,
  rootMenu,
  onToggle,
  onSelectFolder,
  onSelectItem,
  onFilterChange,
  topSlot,
  collapsible = true,
  collapseStorageKey,
  onMoveItem,
  onMoveFolder,
  isLockedFolder,
  isLockedItem,
  dimmedFolderPaths,
  dimmedItemIds,
  navigationMode = 'expand',
  pinnedFolderPaths
}: ExplorerSidebarProps<F, I>) {
  const storageKey = collapseStorageKey ?? `ontology.explorerSidebar.${testIdPrefix}.open`
  const widthStorageKey = `${storageKey}.width`
  const [open, setOpenState] = useState(() => (collapsible ? loadExplorerOpen(storageKey) : true))
  const [width, setWidth] = useState(() =>
    collapsible ? loadExplorerWidth(widthStorageKey) : EXPLORER_DEFAULT_WIDTH
  )
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartRef = useRef({ x: 0, width: EXPLORER_DEFAULT_WIDTH })
  const setOpen = (next: boolean) => {
    setOpenState(next)
    if (collapsible) persistExplorerOpen(storageKey, next)
  }

  useEffect(() => {
    if (!isResizing) return
    const onPointerMove = (event: PointerEvent): void => {
      const delta = event.clientX - resizeStartRef.current.x
      setWidth(clampExplorerWidth(resizeStartRef.current.width + delta))
    }
    const onPointerUp = (): void => setIsResizing(false)
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing])

  useEffect(() => {
    if (collapsible) persistExplorerWidth(widthStorageKey, width)
  }, [collapsible, widthStorageKey, width])

  const handleResizePointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0) return
    event.preventDefault()
    resizeStartRef.current = { x: event.clientX, width }
    setIsResizing(true)
  }

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    let nextWidth: number | undefined
    if (event.key === 'ArrowLeft') nextWidth = width - EXPLORER_KEYBOARD_RESIZE_STEP
    if (event.key === 'ArrowRight') nextWidth = width + EXPLORER_KEYBOARD_RESIZE_STEP
    if (event.key === 'Home') nextWidth = EXPLORER_MIN_WIDTH
    if (event.key === 'End') nextWidth = EXPLORER_MAX_WIDTH
    if (nextWidth === undefined) return
    event.preventDefault()
    setWidth(clampExplorerWidth(nextWidth))
  }

  const tree = useMemo(() => buildExplorerTree<F, I>(folders, items), [folders, items])
  const filtered = useMemo(() => filterExplorerTree(tree, filter), [tree, filter])
  const filterActive = filter.trim() !== ''
  const drilldown = navigationMode === 'drilldown'
  const itemsById = useMemo(() => {
    const map = new Map<string, I>()
    for (const item of items) map.set(item.id, item)
    return map
  }, [items])
  const currentFolderPath = drilldown ? drilldownFolderPath(selection, itemsById) : ''
  const currentNode = useMemo(() => {
    if (!drilldown || filterActive) return filtered
    return findFolderNode(tree, currentFolderPath) ?? tree
  }, [drilldown, filterActive, filtered, tree, currentFolderPath])
  const visibleChildKeys = useMemo(() => {
    const source = drilldown && !filterActive ? currentNode : filtered
    const pinAtRoot = !drilldown || currentFolderPath === ''
    return pinAtRoot
      ? orderPinnedChildKeys(source.children, pinnedFolderPaths ?? [])
      : Object.keys(source.children)
  }, [currentFolderPath, currentNode, drilldown, filterActive, filtered, pinnedFolderPaths])
  const flatFilterRows = useMemo(
    () => (drilldown && filterActive ? flattenExplorerTree(filtered) : []),
    [drilldown, filterActive, filtered]
  )
  const listNode = drilldown && !filterActive ? currentNode : filtered
  const totalCount = folders.length + items.length
  const isEmpty =
    drilldown && filterActive
      ? flatFilterRows.length === 0
      : visibleChildKeys.length === 0 && listNode.items.length === 0
  const dropDestPath = drilldown && !filterActive ? currentFolderPath : ''
  const [dragging, setDraggingState] = useState<DragPayload | null>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  const draggingRef = useRef<DragPayload | null>(null)
  const setDragging = (payload: DragPayload | null) => {
    draggingRef.current = payload
    setDraggingState(payload)
  }

  const ctx: RowContext<F, I> = {
    itemIcon,
    itemTrailing,
    folderTrailing,
    testIdPrefix,
    selection,
    expanded,
    filterActive,
    drilldown,
    folderMenu,
    itemMenu,
    onToggle,
    onSelectFolder,
    onSelectItem,
    onMoveItem,
    onMoveFolder,
    isLockedFolder,
    isLockedItem,
    dimmedFolderPaths,
    dimmedItemIds,
    itemsById,
    dragging,
    draggingRef,
    setDragging,
    dropTarget,
    setDropTarget
  }

  const dropOnRoot = (event: DragEvent) => {
    if (!canDropOnFolder(draggingRef.current, dropDestPath, isLockedFolder)) return
    event.preventDefault()
    const payload = draggingRef.current
    draggingRef.current = null
    setDragging(null)
    setDropTarget(null)
    if (payload === null) return
    if (payload.kind === 'folder') {
      onMoveFolder?.(payload.path, dropDestPath)
      return
    }
    const item = itemsById.get(payload.id)
    if (item) onMoveItem?.(item, dropDestPath)
  }

  const railOpen = !collapsible || open

  const rail = (
    <div
      className={cn(
        'flex h-full min-h-0 shrink-0 flex-col overflow-hidden',
        collapsible &&
          !isResizing &&
          'transition-[width] duration-200 ease-out motion-reduce:transition-none',
        railOpen ? 'bg-bg-elev' : 'bg-transparent',
        !collapsible && 'border-r border-line bg-bg-elev'
      )}
      style={{ width: railOpen ? width : 0 }}
      data-state={collapsible ? (railOpen ? 'open' : 'collapsed') : undefined}
      data-testid={`${testIdPrefix}-rail`}
      role={railOpen ? 'tree' : undefined}
      aria-label={railOpen ? ariaLabel : undefined}
    >
      {railOpen ? (
        <>
          {topSlot}
          <div className="px-3.5 pb-2 pt-3.5">
            <div className="flex items-baseline gap-2">
              <h2 className="m-0 text-13 font-bold uppercase tracking-wider text-ink">{heading}</h2>
              <span className="font-mono text-11 text-ink-3">{totalCount}</span>
              <div className="flex-1" />
              {headerButtons.map((button) => {
                const Icon = button.icon
                return (
                  <Button
                    key={button.label}
                    size="icon"
                    variant="outline"
                    onClick={button.onClick}
                    aria-label={button.label}
                    title={button.label}
                    className="size-5.5"
                  >
                    <Icon className="size-2.75" />
                  </Button>
                )
              })}
            </div>
            <div className="relative mt-2.5">
              <Search
                className="pointer-events-none absolute left-2 top-1/2 size-2.75 -translate-y-1/2 text-ink-3"
                aria-hidden
              />
              <Input
                value={filter}
                onChange={(e) => onFilterChange(e.target.value)}
                placeholder="Filter…"
                aria-label={`Filter ${heading} tree`}
                className="h-6.5 py-0 pl-7 font-mono text-12"
              />
            </div>
          </div>
          <div
            className={cn(
              'flex flex-1 flex-col overflow-auto px-1.5 pb-3.5 pt-1',
              dropTarget === dropDestPath && dragging ? 'ring-1 ring-inset ring-brand' : null
            )}
            data-testid={`${testIdPrefix}-root-drop`}
            onDragOver={(event) => {
              if (!canDropOnFolder(draggingRef.current, dropDestPath, isLockedFolder)) return
              event.preventDefault()
              event.dataTransfer.dropEffect = 'move'
              if (dropTarget !== dropDestPath) setDropTarget(dropDestPath)
            }}
            onDrop={(event) => {
              dropOnRoot(event)
            }}
          >
            {drilldown && !filterActive && currentFolderPath !== '' ? (
              <div className="mb-1 flex items-center gap-1 px-1 py-0.5">
                <Button
                  type="button"
                  size="icon-xs"
                  variant="ghost"
                  data-testid={`${testIdPrefix}-back`}
                  aria-label="Back"
                  title="Back"
                  className="size-4.5 shrink-0"
                  onClick={() => onSelectFolder(parentPathOf(currentFolderPath))}
                >
                  <ChevronLeft className="size-3" />
                </Button>
                <nav
                  aria-label="Folder path"
                  data-testid={`${testIdPrefix}-breadcrumb`}
                  dir="rtl"
                  className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-10 text-ink-3"
                >
                  {/*
                    dir=rtl on the overflow box puts the ellipsis on the start
                    so a long trail shows `… / parent / current`. The inner
                    ltr span keeps segment order and slash separators LTR.
                  */}
                  <span dir="ltr">
                    {breadcrumbSegments(currentFolderPath).map((segment, index) => {
                      const node = findFolderNode(tree, segment.path)
                      const label = node?.folder?.name ?? segment.name
                      return (
                        <span key={segment.path}>
                          {index > 0 ? <span aria-hidden> / </span> : null}
                          <button
                            type="button"
                            className="cursor-pointer border-0 bg-transparent p-0 text-10 text-ink-3 hover:text-ink"
                            onClick={() => onSelectFolder(segment.path)}
                          >
                            {label}
                          </button>
                        </span>
                      )
                    })}
                  </span>
                </nav>
              </div>
            ) : null}
            {isEmpty ? (
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <div className="flex-1 px-2 py-3 font-mono text-11 text-ink-3">
                    {isLoading
                      ? 'Loading…'
                      : filterActive
                        ? 'No matches.'
                        : drilldown && currentFolderPath !== ''
                          ? 'This folder is empty.'
                          : emptyHint}
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent>
                  <ContextMenuLabel>Root</ContextMenuLabel>
                  {renderContextEntries(rootMenu)}
                </ContextMenuContent>
              </ContextMenu>
            ) : drilldown && filterActive ? (
              <>
                {flatFilterRows.map((row) =>
                  row.kind === 'folder' ? (
                    <FolderRow key={row.node.path} node={row.node} depth={0} ctx={ctx} />
                  ) : (
                    <ItemRow key={row.item.id} item={row.item} depth={0} ctx={ctx} />
                  )
                )}
              </>
            ) : (
              <>
                {visibleChildKeys.map((k) => (
                  <FolderRow
                    key={listNode.children[k].path}
                    node={listNode.children[k]}
                    depth={0}
                    ctx={ctx}
                  />
                ))}
                {listNode.items.map((item) => (
                  <ItemRow key={item.id} item={item} depth={0} ctx={ctx} />
                ))}
              </>
            )}
          </div>
        </>
      ) : null}
    </div>
  )

  if (!collapsible) return rail

  // The handle gets its own 24px gutter beside the rail rather than hanging
  // off the rail's edge: the page row is overflow-hidden, so anything pulled
  // outside it with negative margins gets clipped in half. The gutter draws
  // the sidebar's divider line down its middle, exposes it as the resize
  // separator, and centers the collapse handle on it.
  return (
    <>
      {rail}
      <div className="relative w-6 shrink-0 self-stretch">
        {railOpen ? <div className="absolute inset-y-0 left-0 w-3 bg-bg-elev" aria-hidden /> : null}
        {railOpen ? (
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${heading} explorer`}
            aria-valuemin={EXPLORER_MIN_WIDTH}
            aria-valuemax={EXPLORER_MAX_WIDTH}
            aria-valuenow={width}
            tabIndex={0}
            data-testid={`${testIdPrefix}-resize`}
            className="group/resize absolute inset-y-0 left-2 z-10 w-2 cursor-col-resize touch-none select-none focus-visible:outline-none"
            onPointerDown={handleResizePointerDown}
            onKeyDown={handleResizeKeyDown}
            onDoubleClick={() => setWidth(EXPLORER_DEFAULT_WIDTH)}
          >
            <div
              className={cn(
                'mx-auto h-full w-px bg-line transition-colors',
                isResizing
                  ? 'bg-brand'
                  : 'group-hover/resize:bg-brand group-focus-visible/resize:bg-brand'
              )}
              aria-hidden
            />
          </div>
        ) : (
          <div className="absolute inset-y-0 left-3 w-px bg-line" aria-hidden />
        )}
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
          <div className="pointer-events-auto">
            <ExplorerRailToggle
              open={railOpen}
              heading={heading}
              testIdPrefix={testIdPrefix}
              onToggle={() => setOpen(!open)}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default ExplorerSidebar
